import { APP_BASE_HREF } from '@angular/common';
import { Component, Inject, Input } from '@angular/core';
import { AbstractControl, ValidatorFn } from '@angular/forms';
import { Router } from '@angular/router';


import { LoginService, LoginSuccessMessage } from '../../../../Bootstrap/services/login.service';
import { ConfigService } from '../../../../Core/Config/services/config.service';
import { CoreDialogService } from '../../../../Core/Dialogs/services/dialog.service';
import { ConfirmDialogComponent } from '../../../Dialogs/components/ConfirmDialog/confirm-dialog.component';
import { ButtonTypesEnum } from '../../../Forms/enums/button-types.enum';
import { validateEmail } from '../../../Misc/helpers/formatting/email.helper';
import { UserStateModel } from '../../../../Core/Users/models/user-state.model';
import { SessionManagementService, SessionStateEnum } from '../../../../Bootstrap/services/session-management.service';

export function emailValidator(): ValidatorFn {
    return (control: AbstractControl) => {
        const invalid = !validateEmail(control.value);
        return invalid ? {email: invalid} : null;
    };
}

@Component({
    selector: 'agpr-session-expired-dialog',
    templateUrl: 'session-expired-dialog.component.html',
    styleUrls: ['session-expired-dialog.component.scss'],
    standalone: false
})
export class SessionExpiredDialogComponent extends ConfirmDialogComponent {
    loading: boolean = false;

    readonly id = 'ghostInDialog';
    readonly buttonTypesEnum = ButtonTypesEnum;

    @Input() currentUserState: UserStateModel;

    constructor(
        coreDialogService: CoreDialogService,
        public configService: ConfigService,
        @Inject(APP_BASE_HREF) public appBaseHref: string,
        private router: Router,
        private sessionManagementService: SessionManagementService
    ) {
        super(coreDialogService);
        this.sessionManagementService.setSessionState(SessionStateEnum.EXPIRED);
    }

    accept() {
        // We have a back-and-forth conversation between this dialog and the LoginService,
        // via the BroadcastChannel. If the accounts don't match, this dialog sends the
        // expected accountId, and the login service will switch accounts and notify the
        // dialog after the action.

        const channel = new BroadcastChannel(LoginService.LOGIN_CHANNEL_ID);
        const loginWindow = window.open(this.appBaseHref + 'login');
        const listener = e => {
            if (e.origin !== window.location.origin || e.data.key !== LoginService.LOGIN_KEY_ID) {
                return;
            }

            const result: LoginSuccessMessage = e.data.result;
            if (result.action === 'success') {
                this.handleSuccessfulReauth(channel, listener, loginWindow, result);
            } else if (result.action === 'complete') {
                this.completeLoginRefresh(channel, listener, loginWindow);
            }
        };
        channel.addEventListener ('message', listener);
    }

    close() {
        this.closeDialog();
    }

    private handleSuccessfulReauth(
        channel: BroadcastChannel,
        listener: EventListener,
        loginWindow: Window,
        result: LoginSuccessMessage
    ) {
        const impersonator = this.currentUserState.impersonator;

        if (impersonator) {
            channel.postMessage({
                key: LoginService.SESSION_KEY_ID,
                result: {
                    action: 'ghostInRequired',
                    userState: {
                        userId: this.currentUserState.user.id,
                        accountId: this.currentUserState.account.id,
                        impersonatorUserId: impersonator.user.id,
                        impersonatorAccountId: impersonator.account.id
                    },
                } as LoginSuccessMessage,
            });
        } else {
            const baseAccountId = this.currentUserState.account?.id;
            if (baseAccountId && baseAccountId === result.userState.accountId) {
                this.completeLoginRefresh(channel, listener, loginWindow);
            } else {
                channel.postMessage({
                    key: LoginService.SESSION_KEY_ID,
                    result: {
                        action: 'accountMismatch',
                        userState: {
                            accountId: baseAccountId
                        },
                    } as LoginSuccessMessage,
                });
            }
        }
    }

    private completeLoginRefresh(
        channel: BroadcastChannel,
        listener: EventListener,
        loginWindow: Window
    ) {
        channel.removeEventListener('message', listener);
        channel.close();

        this.closeDialog(true);
        this.sessionManagementService.setSessionState(SessionStateEnum.ACTIVE);
        if (loginWindow) {
            loginWindow.close();
        }
    }
}
